import HttpResponse, { ErrorResponse } from './HttpResponse';
import { getAuthToken } from '../utils/httpClientUtil';
import { navigate } from 'gatsby';
/* globals CSS_CONFIG */

interface BaseConfig {
  method: 'GET' | 'POST' | 'PUT' | 'HEAD' | 'OPTIONS';
  headers: Headers;
  body?: string;
}

interface RequestConfig extends BaseConfig {
  credentials?: string;
}

export interface FetchRequestConfig extends BaseConfig {
  credentials: 'include';
}

const ensureTrailingSlash = (url: string): string => url.replace(/\/?$/, '/');
const removeLeadingSlash = (url: string): string => {
  const [slash, ...rest] = url;
  return slash === '/' ? rest.toString() : url;
};

interface HeaderMethodType {
  // 'Akamai-BM-Telemetry': string;
  'X-CF-Turnstile': string;
}

const getHeaderForMethod = (
  requestType: string = 'GET',
  token?: string
): HeaderMethodType | {} => {
  // Only attach if POST/OPTIONS AND token exists
  if (['POST', 'OPTIONS'].includes(requestType) && token) {
    return {
      'X-CF-Turnstile': token,
    };
  }
  return {};
};

const fetchRequest = async (
  url: string,
  config: RequestConfig
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
Promise<any> => {
  try {
    const response = await fetch(url, config as FetchRequestConfig);

    if (response.status < 200 || response.status >= 300) {
      const error: ErrorResponse = {
        status: response.status,
        data: await response.json(),
      };
      return new HttpResponse(null, error);
    }
    const result = await response.json();
    return new HttpResponse(result, undefined, response.status);
  } catch (e: any) {
    const error: ErrorResponse = {
      status: 500,
      data: e,
    };
    return new HttpResponse(null, error);
  }
};

class HttpClient {
  private baseUrl: string = ensureTrailingSlash(CSS_CONFIG.SPAREPARTAPIHOST);
  private addressBaseUrl: string = ensureTrailingSlash(
    CSS_CONFIG.ADDRESSAUTOCOMPLETEAPIHOST
  );
  private verifyBackendAvailability: boolean;

  constructor(verifyBackendAvailability: boolean = true) {
    this.verifyBackendAvailability = verifyBackendAvailability;
  }

  public getHeader(
    requestType: string = 'GET',
    token?: string
  ): FetchRequestConfig {
    return {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Accept-Language': `${CSS_CONFIG.LANGUAGE}-${CSS_CONFIG.COUNTRY}`,
        'Content-Language': `${CSS_CONFIG.LANGUAGE}-${CSS_CONFIG.COUNTRY}`,
        ...getHeaderForMethod(requestType, token),
        ...getAuthToken(),
      }),
    };
  }

  public formatUrl = (url: string): string => {
    return this.baseUrl + removeLeadingSlash(url);
  };

  /**
   * Sends an explicit CORS preflight (OPTIONS) request before a real request.
   * Declares the real request's method and non-safelisted header names via
   * Access-Control-Request-Method / Access-Control-Request-Headers; actual
   * header values are never sent on the preflight. Non-OK responses and
   * transport exceptions are logged via console.warn but never block the main
   * request, so behavior is backwards-compatible if a server rejects OPTIONS.
   */
  public sendPreflight = async (
    url: string,
    method: 'GET' | 'POST' | 'PUT' = 'POST',
    requestHeaderNames: string[] = []
  ): Promise<boolean> => {
    try {
      // Declare non-safelisted header names only; a preflight must not carry
      // the actual Authorization / Turnstile / client-id values.
      const names = new Set<string>(
        requestHeaderNames.map((n) => n.toLowerCase())
      );
      if (
        Object.keys(getAuthToken()).some(
          (k) => k.toLowerCase() === 'authorization'
        )
      ) {
        names.add('authorization');
      }
      // application/json Content-Type is not CORS-safelisted.
      names.add('content-type');

      const headers = new Headers({
        'Access-Control-Request-Method': method,
        'Access-Control-Request-Headers': Array.from(names).sort().join(','),
      });
      const response = await fetch(url, {
        method: 'OPTIONS',
        credentials: 'include',
        headers,
      });
      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.warn(
          `[HttpClient] Preflight rejected for ${url}: ${response.status} ${response.statusText}`
        );
      }
      return response.ok;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`[HttpClient] Preflight failed for ${url}:`, error);
      return false;
    }
  };

  public head(url: string): Promise<any> {
    const finalUrl = this.baseUrl + removeLeadingSlash(url);
    const finalConfig: RequestConfig = {
      ...this.getHeader('HEAD'),
      method: 'HEAD',
    };
    return fetchRequest(finalUrl, finalConfig);
  }

  public verifyBackend = async (url: string = '/greeting'): Promise<any> => {
    const { response, error, status } = await this.head(url);
    if (!status || error || status != 200) {
      navigate('/503');
      return { response, error, status };
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public get = async (url: string): Promise<any> => {
    const finalUrl = this.baseUrl + removeLeadingSlash(url);
    const finalConfig: RequestConfig = {
      ...this.getHeader('GET'),
    };
    return fetchRequest(finalUrl, finalConfig);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public post = async (
    url: string,
    customerData: {},
    token?: string
  ): Promise<any> => {
    const finalUrl = this.baseUrl + removeLeadingSlash(url);
    // Establish CORS handshake before sending the actual POST.
    await this.sendPreflight(finalUrl, 'POST', token ? ['X-CF-Turnstile'] : []);
    const finalConfig: RequestConfig = {
      ...this.getHeader('POST', token), // only sets header if token is defined
      method: 'POST',
      body: JSON.stringify({ ...customerData }),
    };
    return fetchRequest(finalUrl, finalConfig);
  };

  public postaddressapi = async (
    url: string,
    customerData: {}
  ): Promise<any> => {
    const finalUrl = this.addressBaseUrl + removeLeadingSlash(url);
    const addressExtraHeaders: Record<string, string> = {
      'x-client-id': CSS_CONFIG.ADDRESSAUTOCOMPLETEAPICLIENTID,
    };
    // Explicit preflight for the address autocomplete host (different origin).
    await this.sendPreflight(
      finalUrl,
      'POST',
      Object.keys(addressExtraHeaders)
    );
    const finalConfig: RequestConfig = {
      headers: new Headers({
        Accept: 'application/json',
        ...addressExtraHeaders,
        'Content-Type': 'application/json',
        'Accept-Language': `${CSS_CONFIG.LANGUAGE}-${CSS_CONFIG.COUNTRY}`,
        'Content-Language': `${CSS_CONFIG.LANGUAGE}-${CSS_CONFIG.COUNTRY}`,
        ...getAuthToken(),
      }),
      method: 'POST',
      body: JSON.stringify({ ...customerData }),
    };
    return fetchRequest(finalUrl, finalConfig);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public put = async (url: string, customerData: {}): Promise<any> => {
    const finalUrl = this.baseUrl + removeLeadingSlash(url);
    // Establish CORS handshake before sending the actual PUT.
    await this.sendPreflight(finalUrl, 'PUT');
    const finalConfig: RequestConfig = {
      ...this.getHeader('PUT'),
      method: 'PUT',
      body: JSON.stringify({ ...customerData }),
    };
    return fetchRequest(finalUrl, finalConfig);
  };
}

export default HttpClient;
